// Phase 15 Plan 06 Task 1 — Single active-match card row.
//
// Renders an active-match preview: opponent display name, turn-clock
// countdown, whose-turn badge (a Frost pulsing dot when waiting on the
// opponent, an Ember pulse-ring when it is the caller's turn), last-updated
// relative timestamp. Tap → `router.navigate(/match/{match_id})`.
//
// Folk-art reskin (Phase 3): the your-turn ring + pulse use the Ember house
// colour (--color-player-a, prototype .row-your-turn + .anim-pulse); the
// waiting badge is a Frost (--color-player-b) dot (prototype waiting row).
//
// Reduced motion (15-PATTERNS.md): the Ember pulse-ring becomes a static
// border when `$reducedMotion === true` (REQ-reduced-motion); the Frost dot
// float is gated via prefers-reduced-motion + [data-motion="reduced"].
//
// The whole card is the tap target (WCAG 2.5.5 — minimum 48px height).

import { useStore } from '@nanostores/react'
import { type JSX, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { ActiveMatchPreview } from '../../net/home'
import { useRouter } from '../../router/router'
import { $reducedMotion } from '../../stores/motion'
import { Avatar } from '../components/Avatar'

export interface ActiveMatchCardProps {
  readonly match: ActiveMatchPreview
}

/**
 * Format milliseconds as "HHh MMm" when >= 60min else "MMm SSs". Returns
 * "0s" for non-positive durations (turn clock expired — server will
 * resolve, but client renders cleanly).
 */
function formatRemaining(ms: number): string {
  const clamped = Math.max(0, ms)
  const totalSeconds = Math.floor(clamped / 1000)
  const totalMinutes = Math.floor(totalSeconds / 60)
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`
  }
  const seconds = totalSeconds % 60
  return `${totalMinutes}m ${seconds.toString().padStart(2, '0')}s`
}

export function ActiveMatchCard({ match }: ActiveMatchCardProps): JSX.Element {
  const router = useRouter()
  const reducedMotion = useStore($reducedMotion)
  const intl = useIntl()
  const [now, setNow] = useState<number>(() => Date.now())

  // Tick once per second so the countdown stays live. Cleaned up on
  // unmount + when the card un-mounts during LobbyScreen re-render.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const isYourTurn = match.current_turn_slot === match.your_slot
  const remaining = match.last_action_ts + match.turn_clock_ms - now

  // Opponent avatar initial (prototype MatchRow leads each active-match row with
  // the player's folk Avatar). Falls back to '?' for an empty display name.
  const opponentInitial =
    match.opponent_display_name.trim().charAt(0).toUpperCase() || '?'

  // WR-08: use an ICU `select` so both branches of the whose_turn string
  // are localized (the previous template interpolated English literals
  // "your turn" / "their turn" into the i18n placeholder).
  const ariaLabel = intl.formatMessage(
    {
      id: 'social.lobby.active_match_card_aria',
      defaultMessage:
        'Match against {opponent}, {is_your_turn, select, true {your turn} other {their turn}}',
    },
    {
      opponent: match.opponent_display_name,
      is_your_turn: String(isYourTurn),
    },
  )

  const handleClick = (): void => {
    router.navigate(`/match/${match.match_id}`)
  }

  // Pulse-ring: Sun-Yellow box-shadow ring around the card when it is the
  // caller's turn. Reduced-motion → static (no animation property).
  const pulseAnimation =
    isYourTurn && !reducedMotion
      ? 'lobby-card-pulse 1.2s ease-in-out infinite'
      : 'none'

  return (
    <button
      type="button"
      className="row"
      data-testid={`active-match-card-${match.match_id}`}
      data-active-match-card="true"
      data-your-turn={isYourTurn ? 'true' : 'false'}
      onClick={handleClick}
      aria-label={ariaLabel}
      style={{
        width: '100%',
        // `.row` recipe owns layout (flex, gap 13, pad 13×15, card bg, r20,
        // shadow). Buttons need an explicit border reset (no UA chrome).
        border: 'none',
        textAlign: 'left',
        color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-family-body)',
        // your-turn → ember ring (static, so reduced-motion keeps it) layered
        // over the card drop, with the pulse animation in full-motion.
        ...(isYourTurn
          ? {
              boxShadow:
                'var(--shadow-card), 0 0 0 2.5px var(--color-player-a)',
              animation: pulseAnimation,
            }
          : {}),
      }}
    >
      {/* Inline keyframes — scoped to this component via <style> tag.
          Ember pulse-ring expansion (your-turn) + Frost dot float (waiting).
          Both disabled under reduced motion (the ring also has a JS gate via
          $reducedMotion → animation:none on the card). */}
      <style>{`
        @keyframes lobby-card-pulse {
          0%, 100% { box-shadow: var(--shadow-card), 0 0 0 2.5px var(--color-player-a), 0 0 0 0 color-mix(in srgb, var(--color-player-a) 45%, transparent); }
          50%      { box-shadow: var(--shadow-card), 0 0 0 2.5px var(--color-player-a), 0 0 0 6px color-mix(in srgb, var(--color-player-a) 0%, transparent); }
        }
        @keyframes lobby-waiting-dot {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        .lobby-waiting-dot { animation: lobby-waiting-dot 1.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .lobby-waiting-dot { animation: none; } }
        [data-motion="reduced"] .lobby-waiting-dot { animation: none; }
      `}</style>
      <Avatar
        initial={opponentInitial}
        color={isYourTurn ? 'var(--color-player-a)' : 'var(--color-player-b)'}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          flex: 1,
          minWidth: 0,
        }}
      >
        <span
          data-testid="active-match-opponent"
          style={{
            fontFamily: 'var(--font-family-display)',
            fontWeight: 600,
            fontSize: 'var(--font-size-16)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {match.opponent_display_name}
        </span>
        <span
          data-testid="active-match-countdown"
          style={{
            fontSize: 'var(--font-size-13)',
            color: 'var(--color-text-secondary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatRemaining(remaining)}
        </span>
      </div>
      <div
        data-testid="active-match-status-badge"
        aria-hidden="true"
        style={{
          minWidth: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-1)',
          // your-turn → Ember tint "PLAY"-style chip (prototype .chip-you);
          // waiting → transparent shell holding the Frost dot.
          backgroundColor: isYourTurn
            ? 'var(--color-player-a-tint)'
            : 'transparent',
          color: isYourTurn
            ? 'var(--color-player-a-deep)'
            : 'var(--color-text-secondary)',
          border: 'none',
          borderRadius: 'var(--radius-pill)',
          fontFamily: 'Fredoka, sans-serif',
          fontWeight: 700,
          fontSize: 'var(--font-size-13)',
          padding: '0 var(--space-2)',
        }}
      >
        {isYourTurn ? (
          <FormattedMessage
            id="social.lobby.active_match.your_turn"
            defaultMessage="Your turn"
          />
        ) : (
          <span
            aria-hidden="true"
            className="lobby-waiting-dot"
            style={{
              width: '8px',
              height: '8px',
              borderRadius: 'var(--radius-pill)',
              backgroundColor: 'var(--color-player-b)',
              display: 'inline-block',
            }}
          />
        )}
      </div>
    </button>
  )
}
